import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import { SaleOrdersFormTyping } from "../../types";
import { useTranslation } from "react-i18next";
import BillingAddressInput from "./BillingAddressInput";
import DeliveryAddressInput from "./DeliveryAddressInput";
import { useGetModuleCustomerQuery } from "@redux/features/customers/customersApi";
import { getDefaultOption } from "@reusables/dropdowns/BaseDropdown/logic";
import { useShouldSkipCopyAutofill } from "../../CopyHydrationContext";

export default function AddressSection() {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general" });

    const { control, resetField, setValue } = useFormContext<SaleOrdersFormTyping>();

    const customer = useWatch({
        control,
        name: "customer",
    });
    const shouldSkipCopyAutofill = useShouldSkipCopyAutofill(customer?.id);

    const mode = useWatch({
        control,
        name: "mode",
    });

    const { data: extendedCustomerData, isLoading: isExtendedCustomerDataLoading } =
        useGetModuleCustomerQuery(
            { module: "sale", id: customer?.id as number },
            { skip: !customer }
        );

    const activeCustomerData =
        extendedCustomerData?.id === customer?.id ? extendedCustomerData : undefined;
    const previousCustomerIdRef = React.useRef<number | undefined>();
    const hydratedCustomerIdRef = React.useRef<number | undefined>();

    useEffect(() => {
        if (mode !== "creation") {
            previousCustomerIdRef.current = customer?.id;
            hydratedCustomerIdRef.current = customer?.id;
            return;
        }

        if (!customer?.id) {
            previousCustomerIdRef.current = undefined;
            hydratedCustomerIdRef.current = undefined;
            resetField("billing_address");
            resetField("delivery_address");
            setValue("is_billing_for_delivery", false, { shouldDirty: false });
            return;
        }

        if (shouldSkipCopyAutofill) {
            previousCustomerIdRef.current = customer.id;
            hydratedCustomerIdRef.current = customer.id;
            return;
        }

        if (
            previousCustomerIdRef.current !== undefined &&
            previousCustomerIdRef.current !== customer.id
        ) {
            // Customer-scoped address fields cannot be reused across customers. Clear them
            // immediately and let the next customer payload hydrate a fresh set of defaults.
            hydratedCustomerIdRef.current = undefined;
            resetField("billing_address");
            resetField("delivery_address");
            setValue("is_billing_for_delivery", false, { shouldDirty: false });
        }

        previousCustomerIdRef.current = customer.id;
    }, [customer?.id, mode, resetField, setValue, shouldSkipCopyAutofill]);

    useEffect(() => {
        if (
            mode !== "creation" ||
            !activeCustomerData?.billing ||
            hydratedCustomerIdRef.current === activeCustomerData.id
        ) {
            return;
        }

        // Apply customer defaults only once per customer selection; editing mode keeps the order's
        // persisted addresses instead of being overwritten by fresh customer data.
        setValue(
            "billing_address",
            {
                name: activeCustomerData.billing.name,
                street: activeCustomerData.billing.street,
                city: activeCustomerData.billing.city,
                country: activeCustomerData.billing.country,
            },
            { shouldDirty: false }
        );
        setValue(
            "is_billing_for_delivery",
            activeCustomerData.billing.is_used_for_shipping ?? false,
            { shouldDirty: false }
        );

        const defaultDeliveryAddress = getDefaultOption(activeCustomerData.deliveries);

        if (defaultDeliveryAddress) {
            setValue("delivery_address", defaultDeliveryAddress, { shouldDirty: false });
        } else {
            resetField("delivery_address");
        }

        hydratedCustomerIdRef.current = activeCustomerData.id;
    }, [activeCustomerData, mode, resetField, setValue]);

    return (
        <>
            <BillingAddressInput isLoading={isExtendedCustomerDataLoading} />
            <DeliveryAddressInput
                options={activeCustomerData?.deliveries ?? []}
                isLoading={isExtendedCustomerDataLoading}
            />
        </>
    );
}
